import axios from 'axios';

import api from './api';

import IInfosLatLongApi from '../dtos/IInfosLatLongApi';
import { IEnderecoCepApi } from '../dtos/enderecoCepApi';

export interface IState {
  id: number;
  sigla: string;
  nome: string;
  regiao: {
    id: number;
    sigla: string;
    nome: string;
  };
}

export interface ICity {
  id: number;
  nome: string;
  microrregiao: {
    id: number;
    nome: string;
    mesorregiao: {
      id: number;
      nome: string;
      UF: IState;
    };
  };
}

export interface IInfosLatLongApiV2 {
  results: {
    geometry: {
      location: {
        lat: number;
        lng: number;
      };
    };
  }[];
}

const geographicApi = {
  getLatLong: async (
    houseNumber: string,
    address: string,
    neighborhood: string,
    city: string,
    state: string,
  ): Promise<IInfosLatLongApi[]> => {
    const { data: addressComplete } = await api.get(
      `https://nominatim.openstreetmap.org/search.php?q=${address}, ${neighborhood}, ${city}, ${state}, Brazil&polygon_geojson=1&format=jsonv2`,
    );
    if (addressComplete.length > 0) return addressComplete;

    const { data: addressNeighborhood } = await api.get(
      `https://nominatim.openstreetmap.org/search.php?q=${neighborhood}, ${city}, ${state}, Brazil&polygon_geojson=1&format=jsonv2`,
    );
    if (addressNeighborhood.length > 0) return addressNeighborhood;

    const { data: addressCity } = await api.get(
      `https://nominatim.openstreetmap.org/search.php?q=${city}, ${state}, Brazil&polygon_geojson=1&format=jsonv2`,
    );
    if (addressCity.length > 0) return addressCity;

    const { data: addressState } = await api.get(
      `https://nominatim.openstreetmap.org/search.php?q=${state}, Brazil&polygon_geojson=1&format=jsonv2`,
    );

    return addressState;
  },
  getEndereco: async (cep: string): Promise<IEnderecoCepApi> => {
    const { data } = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
    return data;
  },
  getCidades: async (id: string): Promise<ICity[]> => {
    const { data } = await api.get(
      `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${id}/municipios`,
    );
    return data;
  },
  /**
   * Código IBGE (7 dígitos) do município a partir de município + UF.
   *
   * A tela Fiscal exige o código para a NFC-e e digitá-lo à mão é fonte comum
   * de erro. Usa a lista oficial do IBGE por UF e compara sem acento/caixa —
   * "Sao Paulo" casa com "São Paulo". `axios` direto (e não o `api`) para não
   * mandar o Bearer da retaguarda a um serviço de terceiro.
   */
  getCodigoIbgeMunicipio: async (
    uf: string,
    municipio: string,
  ): Promise<string | null> => {
    const normalize = (value: string): string =>
      value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();

    const { data } = await axios.get<ICity[]>(
      `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf.trim().toUpperCase()}/municipios`,
    );

    const alvo = normalize(municipio);
    const encontrado = (data || []).find(
      (city) => normalize(city.nome) === alvo,
    );

    return encontrado ? String(encontrado.id) : null;
  },
  getEstados: async (): Promise<IState[]> => {
    const { data } = await api.get(
      'https://servicodados.ibge.gov.br/api/v1/localidades/estados',
    );
    return data;
  },
  getLatLongV2: async (
    houseNumber: string,
    address: string,
    neighborhood: string,
    city: string,
    state: string,
  ): Promise<IInfosLatLongApiV2> => {
    const { data } = await axios.get(
      'https://maps.googleapis.com/maps/api/geocode/json',
      {
        params: {
          address: `${address}, ${houseNumber}, ${neighborhood}, ${city}, ${state}`,
          key: process.env.REACT_APP_GOOGLE_KEY || '',
          language: 'pt-BR',
        },
      },
    );
    return data;
  },
  getLatLongBairro: async (
    neighborhood: string,
    city: string,
    state: string,
  ): Promise<IInfosLatLongApiV2> => {
    const { data } = await axios.get(
      'https://maps.googleapis.com/maps/api/geocode/json',
      {
        params: {
          address: `${neighborhood}, ${city}, ${state}`,
          key: process.env.REACT_APP_GOOGLE_KEY || '',
          language: 'pt-BR',
        },
      },
    );
    return data;
  },
  getLatLongCidade: async (
    city: string,
    state: string,
  ): Promise<IInfosLatLongApiV2> => {
    const { data } = await axios.get(
      'https://maps.googleapis.com/maps/api/geocode/json',
      {
        params: {
          address: `${city}, ${state}`,
          key: process.env.REACT_APP_GOOGLE_KEY || '',
          language: 'pt-BR',
        },
      },
    );
    return data;
  },
};

export default geographicApi;
